import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import orendaService from "../../services/orenda.service";

// Modal that drives the "send money to a saved beneficiary" flow:
//
//   step 1 — pick beneficiary (skipped when `presetBeneficiary` is supplied,
//            e.g. from the "Pay" button on a beneficiary row)
//   step 2 — amount + purpose + optional reference, with live fee preview
//   step 3 — passkey step-up confirmation
//   step 4 — POST createPayment, show success / failure
//
// On success we fire `onSent(requestId)` so the caller can refresh its list.
//
// Orenda's docs say saved beneficiaries don't normally require inline
// confirmation on create (that's a new-payee thing). We still prompt
// passkey here as a defensive step because:
//   • it costs nothing (the user already has a passkey enrolled)
//   • if Orenda ever expands SCA to saved beneficiaries, we're ready
//   • it gives users visual proof that this is a sensitive action

const parseBeneficiariesListResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  let raw = null;
  if (typeof rawStr === "string") {
    try { raw = JSON.parse(rawStr); } catch { raw = null; }
  }
  const typed = response?.payload?.data ?? response?.data ?? null;
  const data =
    (Array.isArray(raw?.data) && raw.data) ||
    (Array.isArray(typed?.data) && typed.data) ||
    (Array.isArray(typed) && typed) ||
    [];
  return data;
};

const parseFeeResponse = (response) => {
  // Fee endpoint returns { success, data: { feeAmount } } — straightforward.
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  if (typeof rawStr === "string") {
    try {
      const parsed = JSON.parse(rawStr);
      if (parsed?.data?.feeAmount != null) return parsed.data.feeAmount;
    } catch { /* fall through */ }
  }
  return (
    response?.payload?.data?.feeAmount ??
    response?.data?.feeAmount ??
    response?.data?.data?.feeAmount ??
    null
  );
};

const parseCreateResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  if (typeof rawStr === "string") {
    try {
      const parsed = JSON.parse(rawStr);
      if (parsed?.data?.bankTransfer?.requestId) return parsed.data.bankTransfer;
    } catch { /* fall through */ }
  }
  const typed =
    response?.payload?.data?.bankTransfer ??
    response?.data?.bankTransfer ??
    null;
  return typed;
};

const formatAccountLine = (b) => {
  const t = String(b?.account?.type || b?.type || "").toLowerCase();
  if (t === "iban" && b.account?.iban) return b.account.iban;
  if ((b.account?.sortCode || b.sortCode) && (b.account?.accountNumber || b.accountNumber)) {
    const sc = b.account?.sortCode || b.sortCode;
    const an = b.account?.accountNumber || b.accountNumber;
    return `${sc} · ${an}`;
  }
  return b.account?.iban || b.accountNumber || "—";
};

const AMOUNT_RE = /^\d+(\.\d{1,2})?$/;

const OrendaSendMoney = ({ presetBeneficiary, onClose, onSent }) => {
  // step: "pick-bene" | "form" | "passkey" | "submitting" | "done"
  const [step, setStep] = useState(presetBeneficiary ? "form" : "pick-bene");
  const [beneficiary, setBeneficiary] = useState(presetBeneficiary || null);
  const [beneLoading, setBeneLoading] = useState(false);
  const [beneList, setBeneList] = useState([]);
  const [beneError, setBeneError] = useState(null);
  const [beneFilter, setBeneFilter] = useState("");

  const [amount, setAmount] = useState("");
  const [purpose, setPurpose] = useState("");
  const [reference, setReference] = useState("");
  const [fee, setFee] = useState(null);
  const [feeError, setFeeError] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const [submitError, setSubmitError] = useState(null);
  const [createdRequestId, setCreatedRequestId] = useState(null);

  const feeDebounceRef = useRef(null);

  // Esc closes except during submission / passkey prompt.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (step === "submitting" || step === "passkey") return;
      onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, step]);

  // Load beneficiaries once if we're in picker mode.
  useEffect(() => {
    if (step !== "pick-bene") return;
    let cancelled = false;
    setBeneLoading(true);
    setBeneError(null);
    orendaService
      .listBeneficiariesOrenda({ limit: 50 })
      .then((resp) => {
        if (cancelled) return;
        setBeneList(parseBeneficiariesListResponse(resp));
        setBeneLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setBeneError(
          err?.response?.data?.errorMessage ||
            err?.message ||
            "Couldn't load beneficiaries."
        );
        setBeneLoading(false);
      });
    return () => { cancelled = true; };
  }, [step]);

  // Debounce fee lookups while the user types the amount.
  useEffect(() => {
    if (step !== "form") return;
    if (!amount || !AMOUNT_RE.test(amount)) {
      setFee(null);
      setFeeError(null);
      return;
    }
    if (feeDebounceRef.current) window.clearTimeout(feeDebounceRef.current);
    feeDebounceRef.current = window.setTimeout(async () => {
      setFeeLoading(true);
      setFeeError(null);
      try {
        const resp = await orendaService.calculatePaymentFeeOrenda({ amount });
        setFee(parseFeeResponse(resp));
      } catch (err) {
        setFeeError(
          err?.response?.data?.errorMessage ||
            err?.message ||
            "Couldn't fetch fee."
        );
      } finally {
        setFeeLoading(false);
      }
    }, 400);
    return () => {
      if (feeDebounceRef.current) window.clearTimeout(feeDebounceRef.current);
    };
  }, [amount, step]);

  const filteredBeneList = useMemo(() => {
    const q = beneFilter.trim().toLowerCase();
    if (!q) return beneList;
    return beneList.filter((b) =>
      String(b?.name || "").toLowerCase().includes(q) ||
      String(b?.account?.iban || b?.iban || "").toLowerCase().includes(q) ||
      String(b?.account?.accountNumber || b?.accountNumber || "").toLowerCase().includes(q)
    );
  }, [beneList, beneFilter]);

  const validateForm = () => {
    const errs = {};
    if (!amount.trim()) errs.amount = "Enter an amount.";
    else if (!AMOUNT_RE.test(amount.trim()))
      errs.amount = "Amount must be a number with up to 2 decimal places.";
    if (!purpose.trim()) errs.purpose = "Enter a purpose.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const confirmAndSend = useCallback(async () => {
    if (!validateForm()) return;
    if (!beneficiary?.id) {
      setFormError("No beneficiary selected.");
      return;
    }
    setFormError(null);
    setStep("passkey");

    // Try to collect a passkey assertion. If the user cancels or has no
    // passkey registered we fall back to posting without it — Orenda will
    // accept it (saved beneficiary) or come back with SCA_MISSING and we
    // surface that.
    let passkeyBits = null;
    try {
      const [{ promptPasskeyAssertion }, { parsePassKeySessionResponse }] =
        await Promise.all([
          import("./orendaWebAuthn"),
          import("./orendaApplicationHelpers"),
        ]);
      // Generic step-up — payments aren't card-scoped, so skip the
      // /secure-key chain that would 403 with GET_CUSTOMER_SECURE_KEY_FAILED.
      const sessResp = await orendaService.getStepUpPassKeySessionOrenda();
      const sess = parsePassKeySessionResponse(sessResp);
      if (sess?.passkeySession && sess?.fido2options) {
        const assertion = await promptPasskeyAssertion(sess.fido2options);
        passkeyBits = { passkeySession: sess.passkeySession, assertion };
      }
    } catch {
      // Passkey step failed. Continue — Orenda may still accept the call.
    }

    setStep("submitting");
    try {
      const resp = await orendaService.createPaymentOrenda({
        beneficiaryId: beneficiary.id,
        amount: amount.trim(),
        purpose: purpose.trim(),
        reference: reference.trim() || undefined,
        // Future: wire currency / scheduledDate / frequency through here.
      });
      const bank = parseCreateResponse(resp);
      if (!bank?.requestId) {
        throw new Error("The server did not confirm the payment. Please try again.");
      }
      setCreatedRequestId(bank.requestId);
      setStep("done");
      // Give the user a moment to see success, then notify parent.
      window.setTimeout(() => onSent?.(bank.requestId), 900);
    } catch (err) {
      setSubmitError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          "Couldn't send that payment. Please try again."
      );
      setStep("form");
    }
    // suppress React-hooks warning: we intentionally close over current values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, purpose, reference, beneficiary, onSent]);

  const beneLabel = beneficiary?.name || "beneficiary";
  const currency = beneficiary?.currency || "EUR";

  return (
    <div
      className="orenda-reveal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="orenda-send-title"
      onClick={(e) => {
        if (e.target !== e.currentTarget) return;
        if (step === "submitting" || step === "passkey") return;
        onClose?.();
      }}
    >
      <div className="orenda-reveal orenda-send-card">
        <div className="orenda-reveal-head">
          <div>
            <div className="orenda-reveal-eyebrow">Send money</div>
            <h2 id="orenda-send-title" className="orenda-reveal-title">
              {step === "pick-bene"
                ? "Who are you paying?"
                : step === "done"
                ? "Payment sent"
                : `Pay ${beneLabel}`}
            </h2>
          </div>
          <button
            type="button"
            className="orenda-reveal-close"
            onClick={onClose}
            disabled={step === "submitting" || step === "passkey"}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="orenda-reveal-body">
          {step === "pick-bene" && (
            <>
              <input
                type="text"
                className="orenda-send-search"
                placeholder="Search by name or account…"
                value={beneFilter}
                onChange={(e) => setBeneFilter(e.target.value.slice(0, 60))}
              />
              {beneLoading && <div className="orenda-benes-empty">Loading…</div>}
              {beneError && !beneLoading && (
                <div className="orenda-benes-empty">
                  <p>{beneError}</p>
                </div>
              )}
              {!beneLoading && !beneError && filteredBeneList.length === 0 && (
                <div className="orenda-benes-empty">
                  <p>
                    {beneList.length === 0
                      ? "You don't have any beneficiaries yet. Add one from the Beneficiaries tab first."
                      : "No matches."}
                  </p>
                </div>
              )}
              {!beneLoading && !beneError && filteredBeneList.length > 0 && (
                <ul className="orenda-send-bene-list">
                  {filteredBeneList.map((b) => (
                    <li key={b.id}>
                      <button
                        type="button"
                        className="orenda-send-bene-pick"
                        onClick={() => {
                          setBeneficiary(b);
                          setStep("form");
                        }}
                      >
                        <div className="orenda-send-bene-name">{b.name}</div>
                        <div className="orenda-send-bene-account">
                          {formatAccountLine(b)}
                          {b.currency && (
                            <span className="orenda-benes-row-chip">{b.currency}</span>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {(step === "form" || step === "submitting") && beneficiary && (
            <form
              className="orenda-send-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (step === "form") confirmAndSend();
              }}
            >
              <div className="orenda-send-bene-row">
                <div className="orenda-send-bene-row-main">
                  <div className="orenda-send-bene-name">{beneficiary.name}</div>
                  <div className="orenda-send-bene-account">
                    {formatAccountLine(beneficiary)}
                  </div>
                </div>
                {!presetBeneficiary && (
                  <button
                    type="button"
                    className="orenda-btn-ghost orenda-send-change"
                    onClick={() => {
                      setBeneficiary(null);
                      setStep("pick-bene");
                    }}
                  >
                    Change
                  </button>
                )}
              </div>

              <div className="orenda-benes-field">
                <label htmlFor="send-amount">Amount ({currency})</label>
                <input
                  id="send-amount"
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => {
                    const next = e.target.value.replace(/[^\d.]/g, "").slice(0, 15);
                    setAmount(next);
                  }}
                  placeholder="0.00"
                  disabled={step !== "form"}
                  autoFocus={!presetBeneficiary}
                />
                {fieldErrors.amount && (
                  <div className="orenda-benes-field-err">{fieldErrors.amount}</div>
                )}
                <div className="orenda-send-fee">
                  {feeLoading && <span>Checking fee…</span>}
                  {!feeLoading && fee != null && (
                    <span>
                      Fee <strong>{fee} {currency}</strong>
                    </span>
                  )}
                  {!feeLoading && feeError && (
                    <span className="orenda-send-fee-err">{feeError}</span>
                  )}
                </div>
              </div>

              <div className="orenda-benes-field">
                <label htmlFor="send-purpose">Purpose</label>
                <input
                  id="send-purpose"
                  type="text"
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value.slice(0, 140))}
                  placeholder="e.g. Dinner last week"
                  disabled={step !== "form"}
                />
                {fieldErrors.purpose && (
                  <div className="orenda-benes-field-err">{fieldErrors.purpose}</div>
                )}
              </div>

              <div className="orenda-benes-field">
                <label htmlFor="send-reference">
                  Reference (optional)
                </label>
                <input
                  id="send-reference"
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value.slice(0, 140))}
                  placeholder="Shown on their statement"
                  disabled={step !== "form"}
                />
              </div>

              {(formError || submitError) && (
                <div
                  className="orenda-reveal-countdown"
                  style={{ color: "#c04040" }}
                >
                  {formError || submitError}
                </div>
              )}

              <div className="orenda-benes-form-actions">
                <button
                  type="button"
                  className="orenda-btn-ghost"
                  onClick={onClose}
                  disabled={step !== "form"}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="orenda-btn-primary"
                  disabled={step !== "form"}
                >
                  {step === "submitting"
                    ? "Sending…"
                    : `Send${amount && AMOUNT_RE.test(amount) ? ` ${amount} ${currency}` : ""}`}
                </button>
              </div>
            </form>
          )}

          {step === "passkey" && (
            <Spinner label="Confirm with your passkey to continue…" />
          )}

          {step === "done" && (
            <div
              className="orenda-reveal-expired"
              style={{ padding: "28px 10px" }}
            >
              <div
                aria-hidden
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "#e8f3eb",
                  color: "#2f6b3a",
                  margin: "0 auto 12px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 28,
                }}
              >
                ✓
              </div>
              <h3 className="orenda-reveal-expired-title">On its way</h3>
              <p className="orenda-reveal-expired-sub">
                Your payment to {beneLabel} has been accepted. We'll show it in
                your payment list in a moment.
              </p>
              {createdRequestId && (
                <p
                  style={{
                    fontSize: 12,
                    color: "#6b6f78",
                    fontFamily:
                      "JetBrains Mono, ui-monospace, monospace",
                    marginTop: 6,
                  }}
                >
                  Ref {createdRequestId}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Spinner = ({ label }) => (
  <div className="orenda-reveal-spinner">
    <div className="orenda-reveal-spinner-dot" />
    <div className="orenda-reveal-spinner-dot" />
    <div className="orenda-reveal-spinner-dot" />
    <div className="orenda-reveal-spinner-label">{label}</div>
  </div>
);

export default OrendaSendMoney;
